import { EChartsOption } from 'echarts'

import { IChartSeries } from '$/types'
import { useAppSelector } from 'app/hooks'
import {
  makeRUoptions,
  formatWithCommasOrExponential,
  getChartLabelBasedOnRiskUnit
} from 'common/utils/chart/chartUtils'
import { getBarColorScheme } from 'common/utils/chart/bar'
import { QRABarColorScheme } from 'common/utils/colorUtils'
import Echart from 'common/components/Echart'
import GraphContainerCard from 'features/RiskManagement/Components/Graphs/Components/GraphContainerCard'
import { selectRiskUnit } from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'

import tailwind from '_/tailwind.config'

interface IConsequencesChart {
  showLoading: boolean
  categories: string[]
  s1Label?: string
  s2Label?: string
  s1Values: number[]
  s2Values?: number[]
  colorScheme?: QRABarColorScheme
}

/**
 * REVIEW: Check whether we can keep this as a dumb component going forward
 *         May not be possible due to potential unique interaction logic..
 *
 * @returns interactive horizontal bar chart
 */
const ConsequencesChart = ({
  showLoading,
  categories,
  s1Label,
  s2Label,
  s1Values,
  s2Values,
  colorScheme
}: IConsequencesChart) => {
  const riskUnit = useAppSelector(selectRiskUnit)
  const series: IChartSeries[] = []

  const formatterOptions = makeRUoptions('ru')

  if (s1Values != null) {
    series.push({
      label: s1Label,
      values: s1Values
    })
  }

  if (s2Values != null) {
    series.push({
      label: s2Label,
      values: s2Values
    })
  }

  const options: EChartsOption = {
    title: [
      {
        text: getChartLabelBasedOnRiskUnit(riskUnit),
        left: 'center',
        bottom: 8,
        textStyle: {
          fontSize: 12
        }
      }
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      valueFormatter: (val) =>
        formatWithCommasOrExponential(val as number, formatterOptions)
    },
    grid: {
      top: 28,
      bottom: 42,
      containLabel: true
    },
    xAxis: {
      axisLabel: {
        formatter: (val: number) =>
          formatWithCommasOrExponential(val, formatterOptions),
        hideOverlap: true
      }
    },
    yAxis: {
      inverse: true,
      data: categories,
      axisLabel: {
        padding: 6,
        borderRadius: 4,
        backgroundColor: tailwind.theme.colors.gray[100]
      },
      axisLine: {
        // NOTE: this highlights yAxis
        show: false
      },
      axisTick: {
        show: false
      }
    },
    series: series.map((s, index) => {
      return {
        type: 'bar',
        name: s.label,
        data: s.values,
        color: getBarColorScheme(colorScheme)[index],
        barWidth: 16 / series.length,
        barGap: '13%'
      }
    }),
    legend: {
      show: series.length > 1,
      left: 0,
      itemHeight: 12,
      itemWidth: 12
    },
    dataZoom: [
      {
        id: 'yAxis-innerScroll',
        type: 'inside',
        yAxisIndex: [0],
        // prevent axis scale update
        filterMode: 'none',
        // items to show (0 indexed)
        maxValueSpan: 8,
        zoomLock: false,
        zoomOnMouseWheel: false,
        moveOnMouseWheel: true
      },
      {
        id: 'yAxis-outerScroll',
        type: 'slider',
        yAxisIndex: [0],
        // hide start/end label on range select
        showDetail: false,
        showDataShadow: false,
        width: 4,
        // background color of selected range area
        fillerColor: tailwind.theme.colors.gray[300],
        // prevent range change
        zoomLock: true,
        // hide range change handle
        handleSize: 0,
        brushSelect: false
      }
    ]
  }

  return (
    <GraphContainerCard title='Risk by Consequences'>
      <Echart notMerge option={options} showLoading={showLoading} />
    </GraphContainerCard>
  )
}

export default ConsequencesChart
